import { all, call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";
import EntityEmailSync from "@mapmycustomers/shared/types/email/EntityEmailSync";
import Person from "@mapmycustomers/shared/types/entity/Person";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import { EntityEmailsState } from ".";
import {
  closeParentEmailThread,
  dismissEntityEmailsSync,
  fetchEntityEmailSync,
  loadChildEmails,
  loadParentEmailThreads,
  loadSentEmail,
  openParentEmailThread,
  relateEmailEntities,
  searchParentEmailThreads,
  unrelateEmailEntities,
  updatePersonEmail,
} from "./actions";
import {
  getEmailThreadsEntity,
  getEntityEmailSync,
  getParentEmailThread,
  getParentEmailThreads,
  getParentEmailThreadsFolder,
  getParentEmailThreadsNeedRefresh,
  getParentEmailThreadsSearchText,
} from "./selectors";

function* fetchParentEmailThreads({ $offset }: Pick<PlatformListRequest, "$offset"> = {}) {
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const entity: Required<EntityEmailsState>["entity"] = yield select(getEmailThreadsEntity);
  const parentEmailThreadsFolder: EntityEmailsState["parentEmailThreadsFolder"] = yield select(
    getParentEmailThreadsFolder
  );
  const parentEmailThreadsSearchText: EntityEmailsState["parentEmailThreadsSearchText"] =
    yield select(getParentEmailThreadsSearchText);

  const response: ListResponse<EmailThread> = yield callApi(
    "fetchEntityEmailThreads",
    orgId,
    entity.entity,
    entity.id,
    {
      $filters: {
        $and: [
          {
            parent: true,
          },
          {
            threadId: {
              $ne: null,
            },
          },
        ],
      },
      $folder: parentEmailThreadsFolder,
      $limit: 10,
      $offset,
      $order: "-date",
      $search: parentEmailThreadsSearchText,
    }
  );

  return response;
}

function* onFetchEntityEmailSync() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entity: Required<EntityEmailsState>["entity"] = yield select(getEmailThreadsEntity);

    const entityEmailSync: EntityEmailSync = yield callApi(
      "fetchEntityEmailSync",
      orgId,
      entity.entity,
      entity.id
    );

    yield put(fetchEntityEmailSync.success(entityEmailSync));
  } catch (error) {
    yield put(fetchEntityEmailSync.failure());
    yield put(handleError({ error }));
  }
}

function* onDismissEntityEmailsSync() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entityEmailSync: Required<EntityEmailsState>["entityEmailSync"] =
      yield select(getEntityEmailSync);

    yield callApi("dismissEntityEmailSync", orgId, entityEmailSync.id);

    yield put(dismissEntityEmailsSync.success());
  } catch (error) {
    yield put(dismissEntityEmailsSync.failure());
    yield put(handleError({ error }));
  }
}

function* onSearchParentEmailThreads() {
  try {
    const emailThreadsResponse: ListResponse<EmailThread> = yield fetchParentEmailThreads();

    yield put(
      searchParentEmailThreads.success({
        data: emailThreadsResponse.data,
        totalCount: emailThreadsResponse.total,
      })
    );
  } catch (error) {
    yield put(searchParentEmailThreads.failure());
    yield put(handleError({ error }));
  }
}

function* onLoadParentEmailThreads() {
  try {
    const parentEmailThreads: EntityEmailsState["parentEmailThreads"] =
      yield select(getParentEmailThreads);

    const emailThreadsResponse: ListResponse<EmailThread> = yield fetchParentEmailThreads({
      $offset: parentEmailThreads.length,
    });

    yield put(
      loadParentEmailThreads.success({
        data: emailThreadsResponse.data,
        totalCount: emailThreadsResponse.total,
      })
    );
  } catch (error) {
    yield put(loadParentEmailThreads.failure());
    yield put(handleError({ error }));
  }
}

function* onOpenParentEmailThread() {
  yield put(loadChildEmails.request());
}

function* onCloseParentEmailThread() {
  const parentEmailThreadsNeedRefresh: boolean = yield select(getParentEmailThreadsNeedRefresh);
  if (parentEmailThreadsNeedRefresh) {
    yield put(searchParentEmailThreads.request());
  }
}

function* onLoadChildEmails() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const parentEmailThread: EmailThread = yield select(getParentEmailThread);

    const childEmails: Email[] = [];

    while (true) {
      const response: ListResponse<Email> = yield callApi("fetchEmails", orgId, {
        $filters: {
          $and: [
            {
              threadId: {
                $eq: parentEmailThread.threadId,
              },
            },
          ],
        },
        $limit: 1000,
        $offset: childEmails.length,
        $order: "date",
      });

      childEmails.push(
        ...response.data.filter(
          (email) => !childEmails.some((loadedEmailThread) => email.id === loadedEmailThread.id)
        )
      );

      if (!response.total || response.total <= childEmails.length) {
        break;
      }
    }
    yield put(loadChildEmails.success(childEmails));
  } catch (error) {
    yield put(loadChildEmails.failure());
    yield put(handleError({ error }));
  }
}

function* onLoadSentEmail({
  payload: { callback, failureCallback, messageId },
}: ReturnType<typeof loadSentEmail.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: ListResponse<Email> = yield callApi("fetchEmails", orgId, {
      $filters: {
        $and: [
          {
            messageId: {
              $eq: messageId,
            },
          },
        ],
      },
    });

    const email = response.data[0];
    if (!email) {
      yield call(failureCallback);
      yield put(loadSentEmail.failure());
      return;
    }

    yield call(callback);
    yield put(loadSentEmail.success(email));
  } catch (error) {
    yield call(failureCallback);
    yield put(loadSentEmail.failure());
    yield put(handleError({ error }));
  }
}

function* onRelateEmailEntities({
  payload: { callback, companies, failureCallback, people },
}: ReturnType<typeof relateEmailEntities>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);

    const updatedPeople: Person[] = yield all(
      people.map((person) => {
        return callApi("updatePerson", organizationId, undefined, {
          id: person.id,
          accounts: [...person.accounts, ...companies.map((company) => ({ id: company.id }))],
        });
      })
    );
    yield call(callback, updatedPeople);
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
  }
}

function* onUnrelateEmailEntities({
  payload: { callback, companies, failureCallback, people },
}: ReturnType<typeof unrelateEmailEntities>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const updatedPeople: Person[] = yield all(
      people.map((person) => {
        return callApi("updatePerson", organizationId, undefined, {
          id: person.id,
          accounts: person.accounts.filter((account) => {
            return !companies.some((company) => company.id === account.id);
          }),
        });
      })
    );
    yield call(callback, updatedPeople);
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
  }
}

function* onUpdatePersonEmail({
  payload: { id, callback, email, failureCallback },
}: ReturnType<typeof updatePersonEmail>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const updatedPerson: Person = yield callApi("updatePerson", organizationId, undefined, {
      id,
      email,
    });
    yield call(callback, updatedPerson);
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
  }
}

export function* entityEmailsSaga() {
  yield takeLatest(fetchEntityEmailSync.request, onFetchEntityEmailSync);
  yield takeLeading(dismissEntityEmailsSync.request, onDismissEntityEmailsSync);
  yield takeLatest(searchParentEmailThreads.request, onSearchParentEmailThreads);
  yield takeLatest(loadParentEmailThreads.request, onLoadParentEmailThreads);
  yield takeEvery(openParentEmailThread, onOpenParentEmailThread);
  yield takeEvery(closeParentEmailThread, onCloseParentEmailThread);
  yield takeLatest(loadChildEmails.request, onLoadChildEmails);
  yield takeLatest(loadSentEmail.request, onLoadSentEmail);
  yield takeEvery(relateEmailEntities, onRelateEmailEntities);
  yield takeEvery(unrelateEmailEntities, onUnrelateEmailEntities);
  yield takeEvery(updatePersonEmail, onUpdatePersonEmail);
}
